import { useRef, type CSSProperties, type ReactNode } from 'react';
import { useAppContext } from '@/Main';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import type { EChartsOption } from 'echarts';
import {
  breakpointsMediaQuery,
  getColor,
  rgbaColor,
  useMediaQuery
} from '@/helpers/utils';
import { Card } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import CardChartDropDown from '@/components/common/CardChartDropDown';
import { useTooltipHide } from '@/hooks/useTooltipHide';

echarts.use([BarChart, TooltipComponent, GridComponent, LegendComponent]);

export type CompetitorsBarChartData = {
  data_name: string[];
  data_x: string[];
} & Record<string, string[] | number[] | string[] | number>;

type CompetitorsBarChartProps = {
  data: CompetitorsBarChartData;
  style?: CSSProperties;
  title: ReactNode;
  tooltip: string;
};

const localizator = (text: string) => {
  switch (text) {
    case 'Pros':
      return 'رشد';
    case 'Stable':
      return 'بدون تغییر';
    case 'Cons':
      return 'افت';
    default:
      return '';
  }
};

const getOption = (
  data: CompetitorsBarChartData,
  isDark: boolean,
  isMdUp: boolean
): EChartsOption => ({
  color: [
    rgbaColor(getColor('success'), 0.8),
    rgbaColor(getColor('danger'), 0.8),
    rgbaColor(getColor('secondary'), 0.8)
  ],
  legend: {
    data: data.data_name,
    formatter: params => localizator(String(params)),
    icon: 'circle',
    padding: [0, 0, 0, 0],
    bottom: 0,
    textStyle: {
      color: getColor('gray-600'),
      marginBottom: -10
    }
  },
  textStyle: {
    fontFamily: 'dana'
  },
  tooltip: {
    triggerOn: isMdUp ? 'mousemove' : 'click',
    trigger: 'axis',
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100') },
    borderWidth: 1,
    formatter: params => {
      const items = Array.isArray(params) ? params : [params];
      const formattedParams = items
        .map(({ seriesName, value, marker }) => {
          return `<span class="d-flex justify-content-between" style="direction:ltr;margin:0;padding:0"><span class="fw-bolder">${marker} ${localizator(String(seriesName)).toPersianDigit()}:</span> ${String(value).toPersianDigit()}</span>`;
        })
        .join('');
      return `
        <div style="font-family: dana !important;">
        تعداد کلمات بالا 
          <div style="direction:rtl;" class="d-flex flex-column">
              ${formattedParams}
          </div>
        </div>`;
    },
    transitionDuration: 0,
    axisPointer: {
      type: 'none'
    }
  },
  xAxis: {
    data: data.data_x,
    type: 'category',
    axisLabel: {
      color: getColor('gray-600'),
      interval: 0,
      show: true
    },
    splitLine: {
      show: false
    },
    axisLine: {
      lineStyle: {
        color: getColor('gray-300'),
        type: 'dashed'
      }
    },
    axisTick: {
      show: false
    }
  },
  yAxis: {
    type: 'value',
    axisLabel: {
      show: true,
      color: getColor('gray-800'),
      margin: 5
    },
    splitLine: {
      show: true,
      lineStyle: {
        type: 'dashed',
        color: getColor('gray-200')
      }
    },
    axisTick: { show: false },
    axisLine: { show: false },
    min: 0
  },
  series: data.data_name.map(item => ({
    name: item,
    type: 'bar',
    itemStyle: {
      shadowColor: rgbaColor(getColor('gray-1100'), 0.3)
    },
    emphasis: {
      focus: 'series'
    },
    data: data[item] as number[]
  })),
  itemStyle: {
    borderRadius: [3, 3, 0, 0]
  },
  barWidth: '12px',
  grid: {
    top: 20,
    bottom: 40,
    left: 10,
    right: 10,
    containLabel: true
  }
});

const CompetitorsBarChart = ({
  data,
  style,
  title,
  tooltip
}: CompetitorsBarChartProps) => {
  const {
    config: { isDark }
  } = useAppContext();
  const cardRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReactEChartsCore>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);
  return (
    <Card ref={cardRef} className="h-md-100">
      <FalconCardHeader
        title={
          <div>
            {title} <HelpTooltip html={tooltip} />
          </div>
        }
        titleTag="h6"
        className="py-2"
        light
        endEl={<CardChartDropDown cardRef={cardRef} chartRef={chartRef} />}
      />
      <Card.Body className="py-3 position-relative">
        <ReactEChartsCore
          echarts={echarts}
          ref={chartRef}
          option={getOption(data, isDark, isMdUp)}
          style={style}
        />
      </Card.Body>
    </Card>
  );
};

export default CompetitorsBarChart;
